// Whole-document formatting reader for a SELECTED (but not edited) textbox.
//
// Anke, 2026-07-16: with the box selected and no caret inside, the options
// circle must describe the text you actually SEE:
//   - uniform across the box            → highlight that one value;
//   - several values, size / colour     → dash every value present;
//   - several values, any other dim     → show nothing.
// This reader only CLASSIFIES (uniform / mixed); the dash-vs-blank policy lives
// in the options-bar element.
//
// "What you see" means unmarked text counts WITH the textbox-wide default — the
// value `buildTextboxHtmlElement` writes onto the wrapper and that unmarked runs
// therefore render in. A half-red, half-untouched box reports red AND the
// default colour, because both are on screen.
//
// PURE and READ-ONLY, mirroring `readSelectionMarkState` — but keyed on the
// stored `TextBox` (document + container defaults) rather than a live
// `EditorState`, so it works with no editor mounted. The two readers answer
// different questions: that one describes the RUN under the caret, this one the
// WHOLE box.

import type { TextBox } from './textbox.interface.js';
import type { TextDocumentNode, TextDocumentMark } from './text-document.type.js';
import type { SelectionDimension, SelectionMarkState } from './selection-mark-state.type.js';

/**
 * The ten dimensions the box-level circle can highlight: the eight per-run ones
 * plus the two BLOCK-level dimensions (`textAlign`, `listType`), which the
 * per-run reader has no notion of because a caret sits in exactly one block.
 */
export type DocumentDimension = SelectionDimension | 'textAlign' | 'listType';

/** One `SelectionMarkState` per dimension, read across the whole document. */
export type DocumentFormatSnapshot = Record<DocumentDimension, SelectionMarkState>;

const ALL_DIMENSIONS: readonly DocumentDimension[] = [
  'italic',
  'underline',
  'strike',
  'color',
  'fontSize',
  'fontWeight',
  'textTransform',
  'verticalScript',
  'textAlign',
  'listType',
];

/** Presence-only dimensions; the rest carry a value. Mirrors the per-run reader. */
const BOOLEAN_DIMENSIONS = new Set<DocumentDimension>(['italic', 'underline', 'strike']);

/** Nodes that carry a `textAlign` attribute (mirrors `text-document-format.ts`). */
const BLOCK_TYPES = new Set(['paragraph', 'heading']);

/** TipTap list container → the `list-type` action value it corresponds to. */
const LIST_NODE_TYPES: Readonly<Record<string, string>> = {
  bulletList: 'bullet',
  orderedList: 'ordered',
  taskList: 'checklist',
};

/** Concatenated text of a node and its descendants. */
function plainText(node: TextDocumentNode): string {
  let buf = '';
  const walk = (n: TextDocumentNode): void => {
    if (typeof n.text === 'string') buf += n.text;
    if (n.content) for (const c of n.content) walk(c);
  };
  walk(node);
  return buf;
}

/**
 * Drop trailing EMPTY paragraphs, mirroring `deriveAdvisoryListType`. StarterKit's
 * `trailingNode` extension auto-appends an empty paragraph after a list (a
 * document may not end on one). It is an editor artifact, not something the user
 * made — and counting it would make a fully bulleted box read as MIXED (bullet +
 * none), so no list button would light. Ditto alignment: the artifact paragraph
 * carries no `textAlign` and would inherit the container default, faking a
 * disagreement with explicitly aligned paragraphs.
 *
 * A trailing paragraph holding TEXT is genuine content and stays.
 */
function withoutTrailingArtifacts(
  content: readonly TextDocumentNode[],
): readonly TextDocumentNode[] {
  let end = content.length;
  while (end > 0 && content[end - 1]!.type === 'paragraph' && plainText(content[end - 1]!).trim() === '') {
    end -= 1;
  }
  return content.slice(0, end);
}

/**
 * Container-default font size for a textbox that carries none, matching the
 * options-bar's long-standing `?? 16` object-path fallback. Without it a legacy
 * box would report the string `"undefinedpx"` and light no size button.
 */
const DEFAULT_FONT_SIZE = 16;

/** Read a `textStyle` attribute, normalising absent/blank to `undefined`. */
function styleAttr(marks: readonly TextDocumentMark[], key: string): string | undefined {
  const attrs = marks.find((m) => m.type === 'textStyle')?.attrs;
  const raw = attrs?.[key];
  return raw === null || raw === undefined || raw === '' ? undefined : String(raw);
}

/**
 * The values a single text run actually RENDERS with: its own mark when it has
 * one, otherwise the textbox-wide default. Boolean dimensions report `'on'` or
 * `null` (absent), matching the per-run reader's convention.
 *
 * `bold` is checked alongside the `fontWeight` attribute because both really do
 * render bold: the options circle writes weight as a `textStyle.fontWeight`
 * attr, but StarterKit's Ctrl+B shortcut writes a `bold` MARK. Reading only the
 * attr would report "normal" for Ctrl+B'd text.
 */
function runValues(
  marks: readonly TextDocumentMark[],
  obj: Readonly<TextBox>,
): Record<SelectionDimension, string | null> {
  const has = (mark: string): boolean => marks.some((m) => m.type === mark);
  // A missing/blank container value reports `null` (= "nothing to show here")
  // rather than a fabricated one: legacy board JSON may omit these, and the
  // stringified `undefined` would match no button while still counting as a
  // distinct value — inventing a disagreement that is not on screen.
  return {
    color: styleAttr(marks, 'color') ?? obj.color ?? null,
    fontSize: styleAttr(marks, 'fontSize') ?? `${obj.fontSize ?? DEFAULT_FONT_SIZE}px`,
    fontWeight: has('bold')
      ? '700'
      : (styleAttr(marks, 'fontWeight') ?? (obj.fontWeight ? String(obj.fontWeight) : null)),
    textTransform: styleAttr(marks, 'textTransform') ?? obj.textTransform ?? 'none',
    verticalScript: styleAttr(marks, 'verticalScript') ?? obj.verticalScript ?? 'none',
    italic: has('italic') || obj.fontStyle === 'italic' ? 'on' : null,
    underline: has('underline') || obj.textDecoration === 'underline' ? 'on' : null,
    strike: has('strike') || obj.textDecoration === 'line-through' ? 'on' : null,
  };
}

/** Classify one dimension from its per-run/per-block observations. */
function classify(
  dim: DocumentDimension,
  values: ReadonlyArray<string | null>,
): SelectionMarkState {
  const present = [...new Set(values.filter((v): v is string => v !== null))];
  const hasOff = values.some((v) => v === null);
  if (present.length === 0) return { state: 'off' };
  if (present.length === 1 && !hasOff) {
    return BOOLEAN_DIMENSIONS.has(dim) ? { state: 'on' } : { state: 'on', value: present[0]! };
  }
  return BOOLEAN_DIMENSIONS.has(dim) ? { state: 'mixed' } : { state: 'mixed', present };
}

/**
 * Read the formatting of a whole textbox: every text run and every block.
 *
 * An EMPTY box (no text runs yet) reports the container defaults rather than
 * `off` for every dimension — those defaults are exactly what the next typed
 * character will render with, so they are what the circle should show.
 */
export function readDocumentFormatState(obj: Readonly<TextBox>): DocumentFormatSnapshot {
  const buckets = ALL_DIMENSIONS.reduce(
    (acc, dim) => {
      acc[dim] = [];
      return acc;
    },
    {} as Record<DocumentDimension, (string | null)[]>,
  );

  let runCount = 0;
  const pushRun = (marks: readonly TextDocumentMark[]): void => {
    runCount++;
    const values = runValues(marks, obj);
    for (const dim of Object.keys(values) as SelectionDimension[]) {
      buckets[dim].push(values[dim]);
    }
  };

  const walk = (node: TextDocumentNode, listType: string): void => {
    const list = LIST_NODE_TYPES[node.type] ?? listType;
    if (BLOCK_TYPES.has(node.type)) {
      // An unaligned block inherits the box-wide default — see `tiptap-extensions`.
      const align = node.attrs?.['textAlign'] ?? obj.hAlign;
      buckets['textAlign'].push(align == null ? null : String(align));
      buckets['listType'].push(list);
    }
    if (node.type === 'text') {
      pushRun(node.marks ?? []);
      return;
    }
    for (const child of node.content ?? []) walk(child, list);
  };
  // `document` is required by the interface but legacy board JSON can reach the
  // slice without one (`hydrateTextbox` only fills it in when `text` is set), so
  // read it defensively rather than throwing inside a highlight refresh.
  for (const block of withoutTrailingArtifacts(obj.document?.content ?? [])) walk(block, 'none');

  // An empty box still tells you something: its container defaults are exactly
  // what the next typed character and the next paragraph will render with.
  if (runCount === 0) pushRun([]);
  if (buckets['textAlign'].length === 0) {
    buckets['textAlign'].push(obj.hAlign ?? null);
    buckets['listType'].push('none');
  }

  const snapshot = {} as Record<DocumentDimension, SelectionMarkState>;
  for (const dim of ALL_DIMENSIONS) snapshot[dim] = classify(dim, buckets[dim]);
  return snapshot;
}
